import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { relativeTime } from '@/lib/format';
import { useDateLocale } from '@/lib/date-locale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CopyButton } from '@/components/app/copy-button';
import ConfirmDestructiveDialog from '@/components/app/confirm-destructive-dialog';

/** The two permission levels the backend accepts. */
export type ApiKeyScope = 'full' | 'read_only';

export interface ApiKey {
  id: string;
  name: string;
  scope: ApiKeyScope;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface CreatedApiKey extends ApiKey {
  /** The plaintext key — returned exactly once, at creation. */
  key: string;
}

const API_KEYS_ENDPOINT = '/users/me/api-keys';

async function fetchApiKeys(): Promise<ApiKey[]> {
  const resp = await apiFetch(API_KEYS_ENDPOINT);
  return (await resp.json()) as ApiKey[];
}

async function createApiKey(name: string, scope: ApiKeyScope): Promise<CreatedApiKey> {
  const resp = await apiFetch(API_KEYS_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({ name: name.trim(), scope }),
  });
  return (await resp.json()) as CreatedApiKey;
}

async function revokeApiKey(id: string): Promise<void> {
  await apiFetch(`${API_KEYS_ENDPOINT}/${id}`, { method: 'DELETE' });
}

/**
 * Personal API keys (settings/profile). Any signed-in operator mints keys for
 * themselves — the server returns the plaintext key exactly once and stores only
 * its digest. `read_only` keys can only call safe (GET/HEAD/OPTIONS) endpoints.
 */
export default function ApiKeysPage() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<ApiKeyScope>('read_only');
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ApiKey | null>(null);

  const keysQuery = useQuery({ queryKey: ['api-keys'], queryFn: fetchApiKeys });

  const createMutation = useMutation({
    mutationFn: () => createApiKey(name, scope),
    onSuccess: (key) => {
      setCreateOpen(false);
      setName('');
      setCreated(key);
      void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: () => toast.error(t('apiKeys.createError')),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: () => {
      setPendingRevoke(null);
      toast.success(t('apiKeys.revokedToast'));
      void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: () => toast.error(t('apiKeys.revokeError')),
  });

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-semibold tracking-tight text-foreground">
            {t('apiKeys.title')}
          </h1>
          <p className="mt-1 max-w-2xl text-[14px] text-muted-foreground">
            {t('apiKeys.subtitle')}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="api-keys-create">
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('apiKeys.createButton')}
        </Button>
      </div>

      <div className="mt-6 rounded-[10px] border bg-card">
        {keysQuery.isLoading ? (
          <div className="space-y-3 p-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : keysQuery.isError ? (
          <div className="flex flex-col items-center gap-3 p-10 text-center">
            <p className="text-[14px] text-muted-foreground">{t('apiKeys.loadError')}</p>
            <Button variant="outline" onClick={() => void keysQuery.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        ) : (keysQuery.data ?? []).length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-10 text-center">
            <KeyRound className="h-6 w-6 text-faint" aria-hidden="true" />
            <p className="text-[14px] text-muted-foreground">{t('apiKeys.empty')}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('apiKeys.name')}</TableHead>
                <TableHead>{t('apiKeys.scope')}</TableHead>
                <TableHead>{t('apiKeys.createdAt')}</TableHead>
                <TableHead>{t('apiKeys.lastUsedAt')}</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(keysQuery.data ?? []).map((key) => {
                const revoked = key.revokedAt !== null;
                return (
                  <TableRow key={key.id} data-testid={`api-key-row-${key.id}`}>
                    <TableCell className="font-medium">
                      {key.name || t('apiKeys.unnamed')}
                    </TableCell>
                    <TableCell>
                      <Badge variant={key.scope === 'read_only' ? 'secondary' : 'outline'}>
                        {t(`apiKeys.scope_${key.scope}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {relativeTime(key.createdAt, dateLocale)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {key.lastUsedAt
                        ? relativeTime(key.lastUsedAt, dateLocale)
                        : t('apiKeys.neverUsed')}
                    </TableCell>
                    <TableCell className="text-right">
                      {revoked ? (
                        <Badge variant="destructive">{t('apiKeys.revoked')}</Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('apiKeys.revoke')}
                          onClick={() => setPendingRevoke(key)}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('apiKeys.createTitle')}</DialogTitle>
            <DialogDescription>{t('apiKeys.createDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="api-key-name">{t('apiKeys.name')}</Label>
              <Input
                id="api-key-name"
                value={name}
                maxLength={200}
                placeholder={t('apiKeys.namePlaceholder')}
                onChange={(event) => setName(event.target.value)}
                data-testid="api-keys-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="api-key-scope">{t('apiKeys.scope')}</Label>
              <Select value={scope} onValueChange={(value) => setScope(value as ApiKeyScope)}>
                <SelectTrigger id="api-key-scope" data-testid="api-keys-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="read_only">{t('apiKeys.scope_read_only')}</SelectItem>
                  <SelectItem value="full">{t('apiKeys.scope_full')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[12.5px] text-muted-foreground">
                {scope === 'read_only'
                  ? t('apiKeys.scopeReadOnlyHint')
                  : t('apiKeys.scopeFullHint')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || name.trim().length === 0}
              data-testid="api-keys-submit"
            >
              {createMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {t('apiKeys.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time key reveal */}
      <Dialog open={created !== null} onOpenChange={(open) => !open && setCreated(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('apiKeys.keyCreatedTitle')}</DialogTitle>
            <DialogDescription>{t('apiKeys.keyCreatedHint')}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2.5">
            <code className="min-w-0 flex-1 break-all font-mono text-[13px] text-foreground">
              {created?.key}
            </code>
            {created && <CopyButton text={created.key} data-testid="api-keys-copy" />}
          </div>
          <DialogFooter>
            <Button onClick={() => setCreated(null)} data-testid="api-keys-done">
              {t('apiKeys.done')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirmation */}
      <ConfirmDestructiveDialog
        open={pendingRevoke !== null}
        onOpenChange={(open) => !open && setPendingRevoke(null)}
        title={t('apiKeys.revokeTitle')}
        description={t('apiKeys.revokeDescription')}
        confirmValue={t('apiKeys.revokeConfirmValue')}
        confirmLabel={t('apiKeys.revoke')}
        isLoading={revokeMutation.isPending}
        onConfirm={() => pendingRevoke && revokeMutation.mutate(pendingRevoke.id)}
      />
    </div>
  );
}
